import { KIT_SPECIFIC_VERSION_LAYOUT } from 'app/router/layouts'
import { latest } from 'app/releases'
import docs from './docs'
import mockups from './mockups'
import moment from 'moment'
import { ALL_PLAN_SLUGS } from 'app/views/plans/PlansData.js'

/**
 * Defines links between site URLs and associated Vue components.
 *
 * Convenience Links:
 * {@link https://v3.router.vuejs.org/guide/#javascript Vue Router Guide}
 */

export default [
  ...docs,
  ...mockups,
  {
    path: '/',
    name: 'home',
    component: () => import('app/views/home/HomeView')
  },
  {
    path: '/changelog',
    name: 'changelog',
    component: () => import('app/views/changelog/ChangeLogView')
  },
  {
    path: '/v5/changelog',
    name: 'five.changelog',
    component: () => import('app/views/changelog/V5ChangeLogView')
  },
  {
    path: '/sessions/sign-in',
    name: 'sessions.sign-in',
    component: () => import('app/views/sessions/SignInView')
  },
  {
    path: '/sessions/sign-out',
    name: 'sessions.sign-out',
    component: () => import('app/views/sessions/SignOutView')
  },
  {
    path: '/download',
    name: 'latest.download',
    component: () => import('app/views/download/DownloadView'),
    meta: { version: latest.v6.version }
  },
  {
    path: '/v6/download',
    name: 'six.download',
    component: () => import('app/views/download/DownloadView'),
    meta: { version: latest.v6.version }
  },
  {
    path: '/v5/download',
    name: 'five.download',
    component: () => import('app/views/download/DownloadView'),
    meta: { version: latest.v5.version }
  },
  {
    path: '/account',
    name: 'account',
    redirect: { name: 'account.general' }
  },
  {
    path: '/account/billing',
    name: 'account.billing',
    meta: { requiresAuth: true },
    component: () => import('app/views/accounts/AccountView')
  },
  {
    path: '/account/deleted',
    name: 'account.deleted',
    component: () => import('app/views/accounts/AccountDeletedView')
  },
  {
    path: '/account/general',
    name: 'account.general',
    meta: { requiresAuth: true },
    component: () => import('app/views/accounts/AccountView')
  },
  {
    path: '/account/license',
    name: 'account.license',
    meta: { requiresAuth: true },
    component: () => import('app/views/accounts/AccountView')
  },
  {
    path: '/account/settings',
    name: 'account.settings',
    meta: { requiresAuth: true },
    component: () => import('app/views/accounts/AccountView')
  },
  {
    path: '/affiliate',
    name: 'affiliate',
    component: () => import('app/views/affiliate/AffiliateView'),
    redirect: defaultAffiliateSalesRoute,
    meta: {
      requiresAuth: true,
      showBackerServicesShutdownWarning: false
    },
    children: [
      {
        path: 'sales',
        name: 'affiliate.sales.default',
        redirect: defaultAffiliateSalesRoute
      },
      {
        path: 'sales/:year/:month',
        name: 'affiliate.sales',
        component: () => import('app/views/affiliate/AffiliateSales')
      },
      {
        path: 'payments',
        name: 'affiliate.payments',
        component: () => import('app/views/affiliate/AffiliatePayments')
      },
      {
        path: 'details',
        name: 'affiliate.details',
        component: () => import('app/views/affiliate/AffiliateDetails')
      }
    ]
  },
  {
    path: '/affiliate/confirm',
    name: 'affiliate.confirm',
    meta: {
      requiresAuth: true,
      showBackerServicesShutdownWarning: false
    },
    component: () => import('app/views/affiliate/AffiliateConfirmView')
  },
  {
    path: '/affiliate/agreement',
    name: 'affiliate.agreement',
    meta: {
      showBackerServicesShutdownWarning: false
    },
    component: () => import('app/views/affiliate/AffiliateAgreementView')
  },
  {
    path: '/account/claim-account',
    name: 'account.claim-account',
    meta: { denyAuth: true },
    component: () => import('app/views/accounts/setup/ClaimAccountView')
  },
  {
    path: '/account/create',
    name: 'account.create',
    component: () => import('app/views/accounts/AccountCreateView')
  },
  {
    path: '/account/cdn',
    name: 'account.cdn',
    meta: { requiresAuth: true },
    component: () => import('app/views/accounts/AccountCdnView')
  },
  {
    path: '/account/invoices/:id',
    name: 'account.invoice',
    meta: {
      requiresAuth: true,
      showBackerServicesShutdownWarning: false
    },
    component: () => import('app/views/accounts/AccountInvoiceView')
  },
  {
    path: '/account/personal-info',
    name: 'account.personal-info',
    meta: {
      requiresAuth: true,
      showBackerServicesShutdownWarning: false
    },
    component: () => import('app/views/accounts/setup/PersonalInfoView')
  },
  {
    path: '/account/reactivate',
    name: 'account.reactivate',
    meta: {
      requiresAuth: true,
      showBackerServicesShutdownWarning: false
    },
    component: () => import('app/views/accounts/AccountReactivateView')
  },
  {
    path: '/account/seats',
    name: 'account.seats',
    meta: {
      requiresAuth: true,
      showBackerServicesShutdownWarning: false
    },
    component: () => import('app/views/accounts/seats/AccountSeatsView')
  },
  {
    path: '/account/seats/add',
    name: 'account.seats.add',
    meta: { requiresAuth: true },
    component: () => import('app/views/accounts/seats/AddSeatsView')
  },
  {
    path: '/account/set-password',
    name: 'account.set-password',
    meta: {
      denyAuth: true,
      showBackerServicesShutdownWarning: false
    },
    component: () => import('app/views/accounts/setup/SetPasswordView')
  },
  {
    path: '/admin',
    meta: {
      requiresAdmin: true,
      showBackerServicesShutdownWarning: false
    },
    component: () => import('app/views/admin/AdminView'),
    children: [
      {
        path: 'accounts',
        name: 'admin.accounts',
        alias: '/admin',
        component: () => import('app/views/admin/accounts/ListView')
      },
      {
        path: 'accounts/:id',
        name: 'admin.accounts.detail',
        component: () => import('app/views/admin/accounts/DetailView')
      },
      {
        path: 'accounts/:id/edit',
        name: 'admin.accounts.edit',
        component: () => import('app/views/admin/accounts/EditView')
      },
      {
        path: 'accounts/:accountId/affiliates/create',
        name: 'admin.accounts.affiliates.create',
        component: () => import('app/views/admin/affiliates/CreateView')
      },
      {
        path: 'accounts/:accountId/affiliates/:id',
        name: 'admin.accounts.affiliates.detail',
        component: () => import('app/views/admin/affiliates/DetailView')
      },
      {
        path: 'accounts/:accountId/affiliates/:id/edit',
        name: 'admin.accounts.affiliates.edit',
        component: () => import('app/views/admin/affiliates/EditView')
      },
      {
        path: 'affiliates',
        name: 'admin.affiliates',
        component: () => import('app/views/admin/affiliates/ListView')
      },
      {
        path: 'affiliate-payments',
        name: 'admin.affiliate-payments',
        component: () => import('app/views/admin/affiliates/affiliatePayments/AffiliatePaymentView')
      },
      {
        path: 'affiliate-complete-payments',
        name: 'admin.affiliate-complete-payments',
        component: () => import('app/views/admin/affiliates/affiliatePayments/AffiliateCompletedPaymentsView')
      },
      {
        path: 'accounts/:accountId/invoices/:id',
        name: 'admin.accounts.invoices.detail',
        component: () => import('app/views/admin/accounts/invoices/DetailView')
      },
      {
        path: 'accounts/:accountId/invoices/:id/edit',
        name: 'admin.accounts.invoices.edit',
        component: () => import('app/views/admin/accounts/invoices/EditView')
      },
      {
        path: 'accounts/:accountId/pageviews',
        name: 'admin.accounts.pageviews.detail',
        component: () => import('app/views/admin/accounts/pageviews/DetailView')
      },
      {
        path: 'audits',
        name: 'admin.audits',
        component: () => import('app/views/admin/audits/ListView')
      },
      {
        path: 'braintree-subscriptions',
        name: 'admin.braintree-subscriptions',
        component: () => import('app/views/admin/subscriptions/braintreeSubscriptions/ListView')
      },
      {
        path: 'braintree-subscriptions/:id',
        name: 'admin.braintree-subscriptions.detail',
        component: () => import('app/views/admin/subscriptions/braintreeSubscriptions/DetailView')
      },
      {
        path: 'braintree-subscriptions/:id/edit',
        name: 'admin.braintree-subscriptions.edit',
        component: () => import('app/views/admin/subscriptions/braintreeSubscriptions/EditView')
      },
      {
        path: 'dashboard',
        name: 'admin.dashboard',
        component: () => import('app/views/admin/DashboardView')
      },
      {
        path: 'kits',
        name: 'admin.kits',
        component: () => import('app/views/admin/kits/ListView')
      },
      {
        path: 'kits/:id',
        name: 'admin.kits.detail',
        component: () => import('app/views/admin/kits/DetailView')
      },
      {
        path: 'kits/:id/pageview-domains',
        name: 'admin.kits.pageview.domains',
        component: () => import('app/views/admin/kits/pageviewDomains/PageviewDomainsListView')
      },
      {
        path: 'licenses',
        name: 'admin.licenses',
        component: () => import('app/views/admin/licenses/ListView')
      },
      {
        path: 'paypal-subscriptions',
        name: 'admin.paypal-subscriptions',
        component: () => import('app/views/admin/subscriptions/paypalSubscriptions/ListView')
      },
      {
        path: 'paypal-subscriptions/:id',
        name: 'admin.paypal-subscriptions.detail',
        component: () => import('app/views/admin/subscriptions/paypalSubscriptions/DetailView')
      },
      {
        path: 'profiles/:id',
        name: 'admin.profiles.detail',
        component: () => import('app/views/admin/profiles/DetailView')
      },
      {
        path: 'stripe-subscriptions',
        name: 'admin.stripe-subscriptions',
        component: () => import('app/views/admin/subscriptions/stripeSubscriptions/ListView')
      },
      {
        path: 'stripe-subscriptions/:id',
        name: 'admin.stripe-subscriptions.detail',
        component: () => import('app/views/admin/subscriptions/stripeSubscriptions/DetailView')
      },
      {
        path: 'subscriptions',
        name: 'admin.subscriptions',
        component: () => import('app/views/admin/subscriptions/ListView')
      },
      {
        path: 'subscriptions/:id',
        name: 'admin.subscriptions.detail',
        component: () => import('app/views/admin/subscriptions/DetailView')
      },
      {
        path: 'subscriptions/:id/edit',
        name: 'admin.subscriptions.edit',
        component: () => import('app/views/admin/subscriptions/EditView')
      },
      {
        path: 'tokens/:id',
        name: 'admin.tokens.detail',
        component: () => import('app/views/admin/tokens/DetailView')
      },
      {
        path: 'quotes',
        name: 'admin.quotes',
        component: () => import('app/views/admin/quotes/CreateView')
      },
      {
        path: 'quotes/invoice',
        name: 'admin.quotes.invoice',
        component: () => import('app/views/admin/quotes/QuoteInvoiceView')
      }
    ]
  },
  {
    path: '/admin/reports',
    name: 'admin.reports',
    meta: {
      requiresAdmin: true,
      showBackerServicesShutdownWarning: false
    },
    component: () => import('app/views/admin/reports/ReportView')
  },
  {
    path: '/checkout/success',
    name: 'checkout.success',
    meta: { showBackerServicesShutdownWarning: false },
    component: () => import('app/views/checkout/SuccessView')
  },
  {
    path: '/buy/:sku',
    name: 'commission.buy',
    meta: { showBackerServicesShutdownWarning: false },
    component: () => import('app/views/commission/CommissionView')
  },
  {
    path: '/kits/:token/upload',
    name: 'kits.kit.upload',
    meta: {
      requiresAuth: true,
      showBackerServicesShutdownWarning: false
    },
    component: () => import('app/views/kits/UploadView')
  },
  {
    path: '/kits',
    name: 'kits',
    meta: { requiresAuth: true },
    component: () => import('app/views/kits/KitsView')
  },
  {
    path: '/kits/new',
    name: 'kits.new',
    meta: { requiresAuth: true },
    component: () => import('app/views/kits/KitWizard')
  },
  {
    path: '/kits/:token',
    name: 'kits.kit',
    redirect: (to) => {
      return { name: 'kits.kit.setup', params: { token: to.params.token } }
    },
    meta: { requiresAuth: true }
  },
  {
    path: '/kits/:token/settings',
    name: 'kits.kit.settings',
    meta: { requiresAuth: true, layout: KIT_SPECIFIC_VERSION_LAYOUT },
    component: () => import('app/views/kits/KitView')
  },
  {
    path: '/kits/:token/use',
    name: 'kits.kit.use',
    redirect: { name: 'kits.kit.setup', layout: KIT_SPECIFIC_VERSION_LAYOUT }
  },
  {
    path: '/kits/:token/setup',
    name: 'kits.kit.setup',
    meta: { requiresAuth: true, layout: KIT_SPECIFIC_VERSION_LAYOUT },
    component: () => import('app/views/kits/KitView')
  },
  {
    path: '/kits/:token/download',
    name: 'kits.kit.download',
    meta: { requiresAuth: true, layout: KIT_SPECIFIC_VERSION_LAYOUT },
    component: () => import('app/views/kits/KitView')
  },
  {
    path: '/kits/:token/package',
    name: 'kits.kit.package',
    meta: { requiresAuth: true, layout: KIT_SPECIFIC_VERSION_LAYOUT },
    component: () => import('app/views/kits/KitView')
  },
  {
    path: '/kits/:token/uploaded-icons',
    name: 'kits.kit.uploaded.icons',
    meta: { requiresAuth: true, layout: KIT_SPECIFIC_VERSION_LAYOUT },
    component: () => import('app/views/kits/KitView')
  },
  {
    path: '/kits/:token/icons/:version?/:icon?',
    name: 'kits.kit.icons',
    meta: {
      scrollBehavior: () => {
        return false
      },
      requiresAuth: true,
      layout: KIT_SPECIFIC_VERSION_LAYOUT
    },
    component: () => import('app/views/kits/KitView')
  },
  {
    path: '/kits/:token/customicons/:version?/:icon?',
    name: 'kits.kit.customicons',
    meta: {
      scrollBehavior: () => {
        return false
      },
      requiresAuth: true,
      layout: KIT_SPECIFIC_VERSION_LAYOUT
    },
    component: () => import('app/views/kits/KitView')
  },
  {
    path: '/leaderboard',
    name: 'leaderboard',
    redirect: { name: 'leaderboard.new' },
    component: () => import('app/views/community/IconRequestsLeaderboardView'),
    children: [
      {
        path: 'new',
        name: 'leaderboard.new',
        component: () => import('app/views/community/NewIcons')
      },
      {
        path: 'brands',
        name: 'leaderboard.brands',
        component: () => import('app/views/community/BrandIcons')
      },
      {
        path: 'released',
        name: 'leaderboard.released',
        component: () => import('app/views/community/ReleasedIcons')
      },
      {
        path: 'in-progress',
        name: 'leaderboard.inprogress',
        component: () => import('app/views/community/InProgressIcons')
      }
    ]
  },
  {
    path: '/license',
    name: 'license.pro',
    component: () => import('app/views/licenses/ProLicenseView')
  },
  {
    path: '/license/free',
    name: 'license.free',
    component: () => import('app/views/licenses/FreeLicenseView')
  },
  {
    path: '/password/forgot',
    name: 'password.forgot',
    meta: { denyAuth: true },
    component: () => import('app/views/passwords/ForgotPasswordView')
  },
  {
    path: '/password/confirm',
    name: 'password.confirm',
    component: () => import('app/views/passwords/ForgotPasswordConfirmView'),
    meta: { denyAuth: true }
  },
  {
    path: '/password/reset',
    name: 'password.reset',
    meta: { denyAuth: true },
    component: () => import('app/views/passwords/ResetPasswordView')
  },
  {
    path: '/search',
    name: 'latest.search',
    component: () => import('app/views/search/IconSearch'),
    meta: { version: latest.v6.version }
  },
  {
    path: '/icons',
    name: 'latest.icons',
    component: () => import('app/views/icons/IconsLanding'),
    meta: { version: latest.v6.version }
  },
  {
    path: '/icons/packs',
    name: 'latest.icons.packs',
    redirect: (to) => ({
      name: 'latest.icons',
      query: { t: 'packs' },
      hash: '#packs'
    })
  },
  {
    path: '/icons/packs/:pack',
    name: 'latest.icons.packs.detail',
    component: () => import('app/views/icons/packs/IconPackLanding'),
    meta: { version: latest.v6.version }
  },
  {
    path: '/icons/categories',
    name: 'latest.icons.categories',
    redirect: (to) => ({
      name: 'latest.icons',
      query: { t: 'categories' },
      hash: '#categories'
    })
  },
  {
    path: '/icons/categories/:category',
    name: 'latest.icons.categories.detail',
    component: () => import('app/views/icons/CategoryLanding'),
    meta: { version: latest.v6.version }
  },
  {
    path: '/v6/icons/categories/:category',
    name: 'six.icons.categories.detail',
    component: () => import('app/views/icons/CategoryLanding'),
    meta: { version: latest.v6.version }
  },
  {
    path: '/icons/:icon',
    name: 'latest.icons.detail',
    component: () => import('app/views/icons/IconLanding'),
    meta: { version: latest.v6.version }
  },
  {
    path: '/v6/search',
    name: 'six.search',
    component: () => import('app/views/search/IconSearch'),
    meta: { version: latest.v6.version }
  },
  {
    path: '/v6/icons',
    name: 'six.icons',
    component: () => import('app/views/search/IconSearch'),
    meta: { version: latest.v6.version }
  },
  {
    path: '/v6/icons/:icon',
    name: 'six.icons.detail',
    component: () => import('app/views/icons/IconLanding'),
    meta: { version: latest.v6.version }
  },
  {
    path: '/v5/search',
    name: 'five.search',
    component: () => import('app/views/search/IconSearch'),
    meta: { version: latest.v5.version }
  },
  {
    path: '/v5/icons',
    name: 'five.icons',
    component: () => import('app/views/icons/IconsLanding'),
    meta: { version: latest.v5.version }
  },
  {
    path: '/v5/icons/categories/:category',
    name: 'five.icons.categories.detail',
    component: () => import('app/views/icons/CategoryLanding'),
    meta: { version: latest.v5.version }
  },
  {
    path: '/v5/icons/:icon',
    name: 'five.icons.detail',
    component: () => import('app/views/icons/IconLanding'),
    meta: { version: latest.v5.version }
  },
  {
    path: '/plans',
    name: 'plans',
    meta: { showBackerServicesShutdownWarning: false },
    component: () => import('app/views/plans/PlansView')
  },
  {
    path: '/privacy',
    name: 'privacy',
    meta: { showBackerServicesShutdownWarning: false },
    component: () => import('app/views/privacy/PrivacyPolicyView')
  },
  {
    path: '/refunds',
    name: 'refunds',
    meta: { showBackerServicesShutdownWarning: false },
    component: () => import('app/views/refunds/RefundsPolicyView')
  },
  {
    path: '/start',
    name: 'start',
    component: () => import('app/views/start/StartView'),
    beforeEnter: (to, from, next) => {
      const { isSignedIn } = to.meta

      if (isSignedIn) {
        window.location.replace('/kits')
      } else {
        next()
      }
    }
  },
  {
    path: '/start/confirm',
    name: 'start.confirm',
    component: () => import('app/views/start/StartConfirmView')
  },
  {
    path: '/support',
    name: 'support',
    component: () => import('app/views/support/SupportView')
  },
  {
    path: '/tos',
    name: 'tos',
    meta: { showBackerServicesShutdownWarning: false },
    component: () => import('app/views/tos/TermsOfServiceView')
  },
  {
    path: '/dpa',
    name: 'dpa',
    meta: { showBackerServicesShutdownWarning: false },
    component: () => import('app/views/dpa/DataProcessingAgreementView')
  },
  {
    path: '/ux/plancards-wired',
    name: 'ux.plancards-wired',
    meta: { showBackerServicesShutdownWarning: false },
    component: () => import('app/views/ux/PlanCardsWiredView')
  },
  {
    path: '/versions',
    name: 'versions',
    component: () => import('app/views/versions/VersionsView')
  },
  {
    path: '*',
    name: 'not.found',
    component: () => import('app/views/errors/NotFoundView')
  }
].concat(plansRoutes())

function defaultAffiliateSalesRoute(_to) {
  const now = moment()

  return {
    name: 'affiliate.sales',
    params: {
      year: now.format('Y'),
      month: now.format('MM')
    }
  }
}

function plansRoutes() {
  const routes = ALL_PLAN_SLUGS.reduce(
    (acc, slug) =>
      acc.concat({
        path: `/plans/${slug}`,
        name: `plans.${slug}`,
        meta: { showBackerServicesShutdownWarning: false },
        component: () => import('app/views/plans/PlanBuyView')
      }),
    []
  )

  return routes
}
